import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { debounce } from 'lodash';

import { triggerUserConvertedStaticMapToDynamicEvent } from 'Services/analytics/events/vehicleLocationMapEvents';
import { logger } from 'Services/logger/logger';
import { isSSR } from 'Utilities/helpers';
import useResizeObserver from 'Utilities/hooks/useResizeObserver';

import type { Locations } from '../LocationSection.types';

import { ErrorOverlay } from './Components/ErrorOverlay/ErrorOverlay';
import { DynamicMap } from './DynamicMap/DynamicMap';
import { StaticMap } from './StaticMap/StaticMap';
import { STATIC_MAP_RESIZE_REFETCH_TIMEOUT_IN_MS } from './SwitchableMap.consts';
import { getSellerLocation, isValidLocation } from './SwitchableMap.helpers';
import type { InitialZoomLevel } from './SwitchableMap.types';

import styles from './SwitchableMap.module.scss';

export interface SwitchableMapProps {
	distance: number;
	enquiryId: number;
	isDealerMarkerVisible: boolean;
	locations: Locations;
}

/*
 * HOW THIS COMPONENT WORKS:
 * - The component renders a static map by default.
 * - When the user clicks on the static map, the component renders a dynamic map.
 * - Static map waits for dynamic map loaded event and fades out to reveal the dynamic map.
 */

export const SwitchableMap: React.FC<SwitchableMapProps> = ({
	distance,
	enquiryId,
	isDealerMarkerVisible,
	locations,
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const [mapWidth, setMapWidth] = useState(0);
	const [isDynamicMapLoaded, setDynamicMapLoaded] = useState(false);
	const [isDynamicMapVisible, setIsDynamicMapVisible] = useState(false);
	const [initialZoomLevel, setInitialZoomLevel] = useState<InitialZoomLevel>('NO_ZOOM');
	const [isInitiallyExpanded, setIsInitiallyExpanded] = useState(false);
	const [hasMapError, setHasMapError] = useState(false);

	const setMapWidthBasedOnContainerWidth = () => {
		setMapWidth(Math.floor(ref.current?.getBoundingClientRect().width ?? 0));
	};

	const setMapWidthDebounced = useMemo(() => {
		return debounce(() => {
			if (isDynamicMapVisible) return;
			setMapWidthBasedOnContainerWidth();
		}, STATIC_MAP_RESIZE_REFETCH_TIMEOUT_IN_MS);
	}, [isDynamicMapVisible]);

	useResizeObserver({ onResize: setMapWidthDebounced, target: ref });

	useEffect(() => {
		setMapWidthBasedOnContainerWidth();
	}, []);

	const handleStaticMapClick = () => {
		setIsDynamicMapVisible(true);
		triggerUserConvertedStaticMapToDynamicEvent();
	};

	const isStaticMapVisible = !isSSR() && mapWidth > 0;
	const sellerLocation = getSellerLocation(locations.seller);
	const isValidSellerCords = useMemo(() => isValidLocation(sellerLocation), [sellerLocation]);

	useEffect(() => {
		if (!isValidSellerCords) {
			setHasMapError(true);
		}
	}, [isValidSellerCords]);

	if (hasMapError) return <ErrorOverlay />;

	const handleMapError = ({ error, mapType }: { error?: unknown; mapType: 'dynamic' | 'static' }) => {
		if (mapType === 'dynamic') {
			// Dynamic map is a fallback when static map fails, error should only show if dynamic map fails
			setHasMapError(true);
		}

		logger.error({
			error: error as Error,
			message: `Error loading map - ${mapType}`,
			scope: 'SwitchableMap',
		});
	};

	return (
		<div ref={ref} className={styles.map} data-testid="map">
			{isDynamicMapVisible && (
				<DynamicMap
					distance={distance}
					enquiryId={enquiryId}
					initialZoomLevel={initialZoomLevel}
					isDealerMarkerVisible={isDealerMarkerVisible}
					isInitiallyExpanded={isInitiallyExpanded}
					isMapLoaded={isDynamicMapLoaded}
					locations={locations}
					onMapError={(error) => handleMapError({ error, mapType: 'dynamic' })}
					onMapLoaded={() => setDynamicMapLoaded(true)}
				/>
			)}
			{isStaticMapVisible && (
				<StaticMap
					className={cx(styles.staticMap, { [styles.hidden]: isDynamicMapLoaded })}
					distance={distance}
					locations={locations}
					mapWidth={mapWidth}
					onClick={handleStaticMapClick}
					onMapError={(error) => handleMapError({ error, mapType: 'static' })}
					setInitialZoomLevel={setInitialZoomLevel}
					setIsInitiallyExpanded={() => setIsInitiallyExpanded(true)}
				/>
			)}
		</div>
	);
};
